import { Timestamp } from 'firebase/firestore';
import { useTime } from 'src/composables/time';
import { PLANNED_MASTERY_QUANTITY_MAX, PLANNED_MASTERY_QUANTITY_MIN } from 'src/constants/programs';

/**
 * Converts a value to a JS Date, handling Firestore Timestamps, plain objects, and Dates.
 */
const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value.toDate === 'function') return value.toDate();
  if (value.seconds != null) return new Timestamp(value.seconds, value.nanoseconds || 0).toDate();
  return null;
};

/**
 * Calculates the planned mastery date from an active timestamp and a timeframe.
 *
 * @param {Timestamp} activeTimestamp - Firestore Timestamp of when the program became active
 * @param {{ quantity: number|null, unit: string }} timeframe - e.g. { quantity: 3, unit: 'months' }
 * @param {Function} addToDate - timezone aware method for shifting dates (useTime composable)
 * @returns {Timestamp|null}
 */
export const calculatePlannedMasteryDate = (activeTimestamp, timeframe, addToDate) => {
  if (!activeTimestamp || !timeframe?.quantity || !timeframe?.unit) {
    return null;
  }

  const activeDate = toDate(activeTimestamp);
  if (!activeDate) return null;

  let result = new Date(activeDate.getTime());
  switch (timeframe.unit) {
    case 'days':
      result = addToDate(result, { days: timeframe.quantity });
      break;
    case 'weeks':
      result = addToDate(result, { days: timeframe.quantity * 7 });
      break;
    case 'months': {
      result = addToDate(result, { months: timeframe.quantity });
      break;
    }
    default:
      return null;
  }

  return Timestamp.fromDate(result);
};

/**
 * Returns the program's active start date, read from statusChanges.active (the timestamp of
 * the program's most recent transition to 'active').
 *
 * @param {object} program
 * @returns {Timestamp|null}
 */
export const getActiveStartDate = (program) => program?.statusChanges?.active ?? null;

/**
 * Computes the planned mastery date on demand from the program's active start date
 * (statusChanges.active) and plannedMasteryTimeframe.
 *
 * @param {object} program
 * @param {Function} addToDate - timezone aware method for shifting dates (useTime composable)
 * @returns {Timestamp|null}
 */
export const getPlannedMasteryDate = (program, addToDate) => {
  return calculatePlannedMasteryDate(getActiveStartDate(program), program?.plannedMasteryTimeframe, addToDate);
};

/**
 * Coerces the quantity input's value to a number. QInput emits a string even with type="number",
 * and calculatePlannedMasteryDate above adds the quantity to a date part — so a string would
 * concatenate instead of add and silently produce the wrong mastery date.
 *
 * @param {string|number|null} value
 * @returns {number|null} null for anything that isn't a number, so the date stays uncalculated
 */
export const parsePlannedMasteryQuantity = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const quantity = Number(value);
  return Number.isFinite(quantity) ? quantity : null;
};

/**
 * Validates the planned-mastery quantity for the program form. An empty quantity is valid —
 * the timeframe is optional.
 *
 * @param {number|null} value - as stored by parsePlannedMasteryQuantity
 * @returns {boolean}
 */
export const isValidPlannedMasteryQuantity = (value) => {
  if (value === null || value === undefined || value === '') {
    return true;
  }

  return Number.isInteger(value)
    && value >= PLANNED_MASTERY_QUANTITY_MIN
    && value <= PLANNED_MASTERY_QUANTITY_MAX;
};

/**
 * Returns true when the timestamp is a real date that already passed. Lets a caller that has
 * computed a mastery date test it without importing Timestamp itself.
 *
 * @param {Timestamp|null} timestamp
 * @returns {boolean}
 */
export const isTimestampInPast = (timestamp) => (
  timestamp instanceof Timestamp && timestamp.toMillis() < Date.now()
);

/**
 * Returns true when the program has a computed planned-mastery date that lies in the past.
 *
 * @param {object} program
 * @param {Function} addToDate - timezone aware method for shifting dates (useTime composable)
 * @returns {Boolean}
 */
export const isPlannedMasteryInPast = (program, addToDate) => (
  isTimestampInPast(getPlannedMasteryDate(program, addToDate))
);

/**
 * True when saving will replace the displayed active start date with today: the program is being
 * switched into Active, a date from an earlier active spell is still on screen, and the user has
 * not picked one themselves (a picked date is kept on save).
 *
 * @param {object} program - the edited program
 * @param {object} originalProgram - the program as loaded, before the user's edits
 * @param {boolean} activeStartDateManuallyEdited
 * @returns {boolean}
 */
export const willActiveStartDateReset = (program, originalProgram, activeStartDateManuallyEdited) => (
  program?.status === 'active'
  && program.status !== originalProgram?.status
  && !!originalProgram?.statusChanges?.active
  && !activeStartDateManuallyEdited
);

/**
 * The timestamp a status change should record. An Active start date the user picked themselves is
 * kept whenever they picked it; every other status change is stamped with the time of the change,
 * so HR-3775's phase lines land on the transition.
 *
 * @param {object} program - the edited program
 * @param {string} status - the status being changed to
 * @param {boolean} activeStartDateManuallyEdited
 * @returns {Timestamp}
 */
export const resolveStatusChangeTimestamp = (program, status, activeStartDateManuallyEdited) => {
  const pickedDate = status === 'active'
    && activeStartDateManuallyEdited
    && getActiveStartDate(program);

  return pickedDate || Timestamp.now();
};

/**
 * Formats the program's active start date as a "YYYY/MM/DD" string for binding to <q-date>.
 * Returns an empty string when no Timestamp is available.
 */
export const formatActiveStartDateForInput = (program) => {
  const ts = getActiveStartDate(program);
  if (!(ts instanceof Timestamp)) return '';

  const { formatDate } = useTime();

  return formatDate(ts, 'yyyy/MM/dd');
};

/**
 * Parses a "YYYY/MM/DD" string (as emitted by <q-date>) into a Firestore Timestamp.
 * Returns null when the input is empty/falsy.
 */
export const parseActiveStartDateFromInput = (value) => {
  if (!value) return null;

  const { extractDate } = useTime();

  return Timestamp.fromDate(extractDate(value, 'yyyy/MM/dd'));
};

export const formatLongDate = (timestamp) => {
  if (!(timestamp instanceof Timestamp)) return '—';

  const { getLocaleDateString } = useTime();

  return getLocaleDateString(timestamp, { options: { year: 'numeric', month: 'long', day: 'numeric' } });
};

export default {
  calculatePlannedMasteryDate,
  getActiveStartDate,
  getPlannedMasteryDate,
  parsePlannedMasteryQuantity,
  isValidPlannedMasteryQuantity,
  isTimestampInPast,
  isPlannedMasteryInPast,
  willActiveStartDateReset,
  resolveStatusChangeTimestamp,
  formatActiveStartDateForInput,
  parseActiveStartDateFromInput,
  formatLongDate,
};
